import React from 'react';
import { useFormContext } from 'react-hook-form';

import { useAccount } from '@/hooks/useAccount';
import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { AI_DATA } from '@/pages/Onboarding/constants';
import { useOnboarding } from '@/pages/Onboarding/context';
import { useQueryOnboardingCategories } from '@/pages/Onboarding/hooks/useQueryOnboardingCategories';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

import { PreferencesVariantDefault } from './Variants/Default';
import { PreferencesVariantV1 } from './Variants/V1';
import { PreferencesVariantV2 } from './Variants/V2';
import { PreferencesVariantV3 } from './Variants/V3';

export const ScreenPreferences: React.FC = () => {
  const { user } = useAccount();

  React.useEffect(() => {
    if (user && canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Twin Preferences',
      });
    }
    preloadImage(AI_DATA[webCampaign.aiType].noLimit.bgImageSrc);
  }, [user]);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const categories = form.watch('categories');
  const preloadCategories = form.getValues('preloadCategories');

  const handleNextStep = () => {
    userService.createOrUpdate({
      categories,
      onboarding_data: {
        first_categories: categories,
      },
    });
    wizard.nextStep();
  };

  useQueryOnboardingCategories({
    enabled: preloadCategories.length === 0,
    form,
  });

  const variant = webCampaign.setup.preferencesVariant;

  switch (variant) {
    case 'v1':
      return (
        <PreferencesVariantV1
          form={form}
          preloadCategories={preloadCategories}
          categories={categories}
          handleNextStep={handleNextStep}
        />
      );
    case 'v2':
      return (
        <PreferencesVariantV2
          form={form}
          preloadCategories={preloadCategories}
          categories={categories}
          handleNextStep={handleNextStep}
        />
      );
    case 'v3':
      return (
        <PreferencesVariantV3
          form={form}
          preloadCategories={preloadCategories}
          categories={categories}
          handleNextStep={handleNextStep}
        />
      );
    default:
      return (
        <PreferencesVariantDefault
          form={form}
          preloadCategories={preloadCategories}
          categories={categories}
          handleNextStep={handleNextStep}
        />
      );
  }
};
